import { toCamelCaseDeep } from 'src/utils/transform';
import { ApiTargetItemType } from 'src/api/learningSessions/types';
import MemriseApi from '../MemriseApi';
import * as Schema from './schema';
import * as Types from './types';

export default class UserDictionaryClass extends MemriseApi {
  constructor() {
    super({ endpointPrefix: '/me' });
  }

  public async getAllUserLearnables(
    languagePairId: number,
    params: Schema.UserLearnablesRequest,
  ): Promise<Types.UserLearnablesResponse> {
    const response = await this.get<Schema.UserLearnablesResponse, Schema.UserLearnablesRequest>(
      `/language_pairs/${languagePairId}/learnables/`,
      params,
    );
    return toCamelCaseDeep(response);
  }

  public async updateSingleLearnable(
    languagePairId: number,
    learnableIdKey: number,
    learnable: Schema.SingleLearnableStat,
  ) {
    await this.put(`/language_pairs/${languagePairId}/learnables/${learnableIdKey}/`, {
      bodyObject: learnable,
    });
  }

  public async updateLearnables(languagePairId: number, learnables: Schema.LearnablesStat) {
    await this.put(`/language_pairs/${languagePairId}/learnables/`, {
      bodyObject: learnables,
    });
  }

  public async getRandomKnownLearnables(
    languagePairId: number,
    options: { typesToExclude?: ApiTargetItemType[]; forPronunciation: boolean },
  ): Promise<Types.RecentLearnablesResponse> {
    const response = await this.get<
      Schema.RecentLearnablesResponse,
      Schema.RecentLearnablesRequest
    >(`/language_pairs/${languagePairId}/recent_learnables/`, {
      types_to_exclude: options.typesToExclude || [],
      for_pronunciation: options.forPronunciation,
    });
    return toCamelCaseDeep(response);
  }

  public async getRecentLearnableDetails(
    languagePairId: number,
  ): Promise<Types.RecentLearnableDetailsResponse> {
    const response = await this.get<Schema.RecentLearnableDetailsResponse>(
      `/language_pairs/${languagePairId}/recent_learnables/details/`,
    );
    return toCamelCaseDeep(response);
  }

  public async getLearnablesPronunciationData(
    languagePairId: number,
    learnableIds: number[],
    mode: Types.PronunciationMode,
    sessionId: string,
  ): Promise<Types.LearnablePronunciationData[]> {
    const response = await this.get<
      Schema.LearnablesPronunciationDataResponse,
      Schema.LearnablesPronunciationDataRequest
    >(
      `/language_pairs/${languagePairId}/learnables/${learnableIds.join(',')}/pronunciation_data/`,
      { mode, session_id: sessionId },
    );

    const initialPositionComparator = (
      a: Types.LearnablePronunciationData,
      b: Types.LearnablePronunciationData,
    ) => learnableIds.indexOf(a.learnableIdKey) - learnableIds.indexOf(b.learnableIdKey);

    return toCamelCaseDeep(response.learnables).sort(initialPositionComparator);
  }

  public async sendLearnablePronunciation({
    languagePairId,
    sessionId,
    learnableId,
    wavAudioBlob,
    retryCount,
  }: {
    languagePairId: number;
    sessionId: string;
    learnableId: number;
    wavAudioBlob: Blob;
    retryCount: number;
  }): Promise<Types.LearnablePronunciationResponse> {
    // the name of the file is not important as long as it uses .wav extension
    const fileName = 'recording.wav';
    const formData = new FormData();
    formData.append('file', wavAudioBlob, fileName);

    const response = await this.postRawData<Schema.LearnablePronunciationResponse>(
      `/language_pairs/${languagePairId}/learnables/${learnableId}/pronunciation/?session_id=${encodeURIComponent(sessionId)}&retry_count=${retryCount}`,
      {
        body: formData,
      },
    );

    return toCamelCaseDeep(response);
  }
}

export const UserDictionaryApi = new UserDictionaryClass();
